import { ChangeDetectionStrategy, Component, computed, effect, linkedSignal, signal, viewChild } from "@angular/core";
import { ModalComponent } from "../../../modules/modal/modal.component";
import { TypeManagerDecorator } from "../../../main/type.map.service";
import { ModalHeaderComponent } from "../../../modules/modal/modal.header.component";
import { S25LoadingInlineModule } from "../../../modules/s25-loading/s25.loading.inline.module";
import { S25ListDataComponent } from "../../../modules/s25-table-list/s25.list.data.component";
import { EventCopyOccurrencePayload } from "../../../pojo/ReservationI";
import { CopyRelatedListEnhancer } from "./copy.related.list.enhancer";
import { EventCopyOccurrenceMappingComponent } from "./event.copy.occurrence.mapping.component";
import { EventCopyOptionsComponent } from "./event.copy.options.component";
import { ActionType, EventCopyEditContext, RoseEventCopyEditFeature } from "./rose.event.copy.edit.feature";

export interface ModalCopyEditEventParams {
    eventId: number;
    type: ActionType;
}

@TypeManagerDecorator("modal-copy-edit-event")
@Component({
    selector: "modal-copy-edit-event",
    standalone: true,
    imports: [
        ModalHeaderComponent,
        S25LoadingInlineModule,
        S25ListDataComponent,
        EventCopyOccurrenceMappingComponent,
        EventCopyOptionsComponent,
    ],
    template: `
        <div class="s25-ng">
            <s25-modal-header [data]="headerData()"></s25-modal-header>
            <div class="modal-body ngCopyEditEventModal">
                @if (roseEdit.event.resource.isLoading()) {
                    <s25-ng-loading-inline-static />
                } @else if (type() === "clone") {
                    <div class="ngIntroHeader">
                        <div>
                            <b>{{ roseEdit.eventName() }}</b>
                            <span>{{ roseEdit.headerMsg() }}</span>
                        </div>
                    </div>

                    @if (roseEdit.canRelateEvents()) {
                        <s25-ng-event-copy-options
                            [(isStandAlone)]="isStandAlone"
                            [hasRelationships]="!!roseEdit.relationships()?.length"
                        />
                    }

                    @if (eventId()) {
                        <s25-ng-event-copy-occurrence-mapping [eventId]="eventId()!" />
                    }

                    @if (roseEdit.relationships()?.length && roseEdit.listQuery()) {
                        <section class="copy-event-section">
                            <p class="copy-event-section__title">Related events</p>
                            <div class="ngCopyEditEventBox">
                                <s25-ng-list-data
                                    #listComponent
                                    [query]="roseEdit.listQuery()!"
                                    [hasSelect]="true"
                                    [enhancer]="copyListEnhancer"
                                    [actions]="roseEdit.checkListActions()"
                                    (selectedChange)="roseEdit.handleSelectedChange($event)"
                                />
                            </div>
                        </section>
                    }

                    <div class="copy-event-actions">
                        <button
                            id="event-copy-button"
                            class="aw-button aw-button--primary"
                            [disabled]="!canCopyEvent()"
                            (click)="currentCopyAction().fn()"
                        >
                            {{ currentCopyAction().text }}
                        </button>
                    </div>
                } @else {
                    <div class="ngIntroHeader">
                        <div>
                            <b>{{ roseEdit.eventName() }}</b>
                            <span>{{ roseEdit.headerMsg() }}</span>
                        </div>
                    </div>

                    @if (!roseEdit.relationships()?.length) {
                        <div class="ngQuickSearchActions">
                            <div>
                                <button (click)="roseEdit.processCurrent()" class="ngCheckActionButton btn btn-default">
                                    {{ roseEdit.Buttons.edit.onlyCurrent.text }}
                                </button>
                                <span class="ngCheckActionSubText ng25LiveGrey">
                                    {{ roseEdit.Buttons.edit.onlyCurrent.subText }}
                                </span>
                            </div>
                        </div>
                    }

                    @if (roseEdit.relationships()?.length && roseEdit.listQuery()) {
                        <div class="ngCopyEditEventBox">
                            <s25-ng-list-data
                                #listComponent
                                [query]="roseEdit.listQuery()!"
                                [hasSelect]="true"
                                [actions]="roseEdit.checkListActions()"
                                [showActions]="'top'"
                                (selectedChange)="roseEdit.handleSelectedChange($event)"
                            />
                        </div>
                    }
                }
            </div>
        </div>
    `,
    styleUrls: ["./modal.copy.edit.event.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ModalCopyEditEventComponent
    extends ModalComponent<ModalCopyEditEventParams>
    implements EventCopyEditContext
{
    public static size = "xl" as const;
    readonly omitListCloneActions = true;

    eventId = computed(() => this.params()?.eventId);
    type = computed(() => this.params()?.type);

    readonly listComponent = viewChild(S25ListDataComponent);
    readonly occurrenceMapping = viewChild(EventCopyOccurrenceMappingComponent);

    readonly copyListEnhancer = new CopyRelatedListEnhancer({
        showMappingStartDate: signal(true),
        dayOffset: computed(() => this.occurrenceMapping()?.dayOffset() ?? 0),
        readOnlyEventId: this.eventId,
        pinFirstItemId: this.eventId,
    });

    readonly roseEdit = new RoseEventCopyEditFeature(this);

    readonly canCopyEvent = computed(() => this.occurrenceMapping()?.canCopy() ?? true);
    readonly hasChecked = computed(() => this.roseEdit.checkedItemsSet().size > 0);
    readonly isStandAlone = linkedSignal<boolean | undefined>(() => !this.hasChecked());
    readonly copyAction = computed(() => {
        if (!this.isStandAlone() && this.roseEdit.canRelateEvents()) {
            return this.hasChecked() ? "copyEditSelected" : "copyAndRelate";
        }
        return "onlyCurrent";
    });
    readonly currentCopyAction = computed<{ text: string; fn: () => void }>(() => {
        if (this.copyAction() === "copyEditSelected") {
            return {
                text: this.roseEdit.Buttons.clone.actionOnSelected.text,
                fn: this.roseEdit.copySelected,
            };
        } else if (this.copyAction() === "copyAndRelate") {
            return {
                text: this.roseEdit.Buttons.clone.actionAndRelate.text,
                fn: this.roseEdit.copyAndRelateBack,
            };
        } else {
            return {
                text: this.roseEdit.Buttons.clone.onlyCurrent.text,
                fn: this.roseEdit.processCurrent,
            };
        }
    });

    readonly headerData = computed(
        () =>
            ({
                ...this.params(),
                title: this.params()?.title || (this.params()?.type === "clone" ? "Copy" : "Edit") + " Related Events",
                helpLink: this.params()?.type === "clone" ? "clone_related_events" : "edit_related_events",
                iconClass: this.params()?.type === "clone" ? "ngCopyIcon" : "ngEditIcon",
            }) as const,
    );

    constructor() {
        super();
        effect(() => {
            if (this.isStandAlone()) {
                this.listComponent()?.clearSelections();
            }
        });
    }

    getRoseOccurrencePayload(itemIds?: number[]): EventCopyOccurrencePayload | undefined {
        const payload = this.occurrenceMapping()?.getRoseOccurrencePayload(itemIds);
        const eventDayOffsets = this.copyListEnhancer.getEventDayOffsets();
        const hasEventOffsets = !!eventDayOffsets && Object.keys(eventDayOffsets).length > 0;
        if (!payload && !hasEventOffsets) {
            return undefined;
        }
        // Keep occurrence-mapping payload untouched when the related-events list has no offsets to merge
        if (payload && !hasEventOffsets) {
            return payload;
        }
        return {
            segments: payload?.segments ?? [],
            dayOffset: payload?.dayOffset ?? 0,
            eventDayOffsets,
        };
    }

    close() {
        this.data.closeModal();
    }
}
